import { Gltf } from './GltfTypes';

const GLB_MAGIC = 0x46546c67; // "glTF"
const GLB_VERSION = 2;
const JSON_CHUNK_TYPE = 0x4e4f534a; // "JSON"
const BIN_CHUNK_TYPE = 0x004e4942; // "BIN\0"

function padTo4(length: number): number {
  return length + ((4 - (length % 4)) % 4);
}

export function encodeGlb(json: Gltf, binaries: Uint8Array[]): Uint8Array {
  // Encode JSON
  const jsonString = JSON.stringify(json);
  const jsonBytes = new TextEncoder().encode(jsonString);
  const jsonPadded = padTo4(jsonBytes.byteLength);

  // Combine binaries
  const totalBinLength = binaries.reduce((sum, b) => sum + b.byteLength, 0);
  const binPadded = totalBinLength > 0 ? padTo4(totalBinLength) : 0;

  // GLB layout: header (12) + JSON chunk (8 + data) + BIN chunk (8 + data)
  const headerSize = 12;
  const jsonChunkSize = 8 + jsonPadded;
  const binChunkSize = totalBinLength > 0 ? 8 + binPadded : 0;
  const totalLength = headerSize + jsonChunkSize + binChunkSize;

  const glb = new ArrayBuffer(totalLength);
  const view = new DataView(glb);
  const bytes = new Uint8Array(glb);
  let offset = 0;

  // Header
  view.setUint32(offset, GLB_MAGIC, true);
  offset += 4;
  view.setUint32(offset, GLB_VERSION, true);
  offset += 4;
  view.setUint32(offset, totalLength, true);
  offset += 4;

  // JSON chunk
  view.setUint32(offset, jsonPadded, true);
  offset += 4;
  view.setUint32(offset, JSON_CHUNK_TYPE, true);
  offset += 4;
  bytes.set(jsonBytes, offset);
  // Pad with spaces (0x20) per glTF spec
  for (let i = jsonBytes.byteLength; i < jsonPadded; i++) {
    bytes[offset + i] = 0x20;
  }
  offset += jsonPadded;

  // BIN chunk (if present)
  if (totalBinLength > 0) {
    view.setUint32(offset, binPadded, true);
    offset += 4;
    view.setUint32(offset, BIN_CHUNK_TYPE, true);
    offset += 4;
    let binOffset = offset;
    for (const binary of binaries) {
      bytes.set(binary, binOffset);
      binOffset += binary.byteLength;
    }
  }

  return new Uint8Array(glb);
}

export function downloadGlb(data: Uint8Array, filename: string) {
  const blob = new Blob([data as BlobPart], {
    type: 'application/octet-stream',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
